import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import { SHOW_PRICES } from "@/lib/features";

/** Premium food-photo lightbox: dark blurred backdrop, ESC/overlay close, wheel + double-click + pinch zoom, drag to pan. */
export function ImageLightbox({ item, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);
  useEffect(() => { reset(); }, [item, reset]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [item, onClose]);

  if (!item) return null;
  const clamp = (v) => Math.min(4, Math.max(1, v));
  const zoomTo = (next) => { const s = clamp(next); setScale(s); if (s === 1) setPos({ x: 0, y: 0 }); };

  const onWheel = (e) => { e.preventDefault(); zoomTo(scale - e.deltaY * 0.0015); };
  const onDoubleClick = () => zoomTo(scale > 1 ? 1 : 2.2);
  const onPointerDown = (e) => { if (scale === 1) return; drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onPointerMove = (e) => { if (!drag.current) return; setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onPointerUp = () => { drag.current = null; };
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => { if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale }; };
  const onTouchMove = (e) => { if (e.touches.length === 2 && pinch.current) { e.preventDefault(); zoomTo(pinch.current.s * (dist(e.touches) / pinch.current.d)); } };
  const onTouchEnd = () => { pinch.current = null; };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/85 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose} data-testid="image-lightbox" role="dialog" aria-modal="true" aria-label={item.name}>
      <button onClick={onClose} className="absolute top-3 right-3 sm:top-5 sm:right-5 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Close" data-testid="lightbox-close">
        <X className="w-5 h-5"/>
      </button>
      <div className="absolute top-3 left-3 sm:top-5 sm:left-5 z-10 hidden sm:flex items-center gap-1 rounded-full bg-white/10 text-white text-xs" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => zoomTo(scale - 0.5)} className="w-9 h-9 flex items-center justify-center hover:bg-white/10 rounded-full" aria-label="Zoom out" data-testid="lightbox-zoom-out"><ZoomOut className="w-4 h-4"/></button>
        <span className="w-10 text-center tabular-nums">{Math.round(scale * 100)}%</span>
        <button onClick={() => zoomTo(scale + 0.5)} className="w-9 h-9 flex items-center justify-center hover:bg-white/10 rounded-full" aria-label="Zoom in" data-testid="lightbox-zoom-in"><ZoomIn className="w-4 h-4"/></button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-10 overflow-hidden" style={{ touchAction: "none" }}
        onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <img
          src={item.image_url} alt={item.name} draggable={false}
          onClick={(e) => e.stopPropagation()} onDoubleClick={onDoubleClick}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          className={`max-w-full max-h-full w-auto h-auto object-contain rounded-xl shadow-2xl select-none transition-transform duration-150 ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
          data-testid="lightbox-image"
        />
      </div>

      <div className="shrink-0 px-5 pb-6 sm:pb-8 text-center text-white animate-in slide-in-from-bottom-2 duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <h3 className="font-display font-semibold text-xl sm:text-2xl" data-testid="lightbox-name">{item.name}</h3>
          {SHOW_PRICES && item.price != null && <span className="font-display font-bold text-[hsl(var(--primary))] text-lg" data-testid="lightbox-price">${Number(item.price).toFixed(2)}</span>}
        </div>
        {item.description && <p className="text-white/70 text-sm mt-1 max-w-xl mx-auto line-clamp-2" data-testid="lightbox-description">{item.description}</p>}
      </div>
    </div>,
    document.body,
  );
}
